import { Box } from '@mui/material';
import { DateTime } from 'luxon';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useLiveNotificationsUtils from '../../../contexts/LiveNotificationsContext/useLiveNotificationsUtils';
import useDate from '../../../hooks/useDate/useDate';
import { LiveNotificationResponse } from '../../../models/NotificationsModels';
import {
  NotificationBody,
  NotificationContainer,
  NotificationDate,
  NotificationTitle,
  UnreadNotification,
} from './NotificationStyles';
import LiveNotificationsTemplate from '../../../contexts/LiveNotificationsContext/LiveNotificationsTemplate';

interface NotificationProps {
  notification: LiveNotificationResponse;
  markAsRead: (id: string) => void;
}
function Notification({ notification, markAsRead }: NotificationProps) {
  const [t] = useTranslation();
  const { timeAgo } = useDate();
  const { isDeviceJourney, liveNotificationTitleAdapter, isSimMismatch, isScheduleJourney } =
    useLiveNotificationsUtils();
  const isRead = useMemo(() => notification.read, [notification]);

  const isImpersonating = useMemo(() => sessionStorage.getItem('impersonateId') !== null, []);

  /**
   * On link click handler
   */
  const onLinkClick = () => {
    if (!notification.read && !isImpersonating) markAsRead(notification.id);
    /* dispatch(toggleDrawer()); */
  };

  /**
   * Retrieves the time ago value
   */
  const notificationDateAdapter = () => {
    if (DateTime.fromISO(notification.dateCreated).isValid) {
      return timeAgo(DateTime.fromISO(notification.dateCreated));
    }
    return t('common.na');
  };

  /**
   * Returns the template jsx
   */
  const createNotificationTemplate = useCallback(() => {
    if (
      isDeviceJourney(notification.type) ||
      isSimMismatch(notification.type) ||
      isScheduleJourney(notification.type)
    ) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <NotificationTitle>{liveNotificationTitleAdapter(notification)}</NotificationTitle>
          <NotificationBody>
            <LiveNotificationsTemplate liveNotification={notification} onNavigate={onLinkClick} />
          </NotificationBody>
          <NotificationDate>{notificationDateAdapter()}</NotificationDate>
        </Box>
      );
    }

    return <div />;
  }, [notification]);

  return (
    <NotificationContainer onClick={() => (!isRead && !isImpersonating ? markAsRead(notification.id) : () => {})}>
      <Box sx={{ padding: '0.4rem 1rem 1rem 0rem' }}>
        <UnreadNotification read={isRead} data-testid="unreadNotification" />
      </Box>
      {createNotificationTemplate()}
    </NotificationContainer>
  );
}

export default Notification;
